import {
  type CreateMaintenanceCustomerRequest,
  createMaintenanceCustomerRequestSchema,
  type MaintenanceCustomer,
} from '@gb-ticket/shared-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { type Resolver, FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import {
  MaintenanceCustomerAddressFields,
  MaintenanceCustomerContactFields,
  MaintenanceCustomerStammdatenFields,
} from '@/products/maintenance/features/forms/customer-form';

const emptyMaintenanceCustomerFormValues: CreateMaintenanceCustomerRequest = {
  name: '',
  customerNumber: null,
  address: {
    line1: '',
    line2: null,
    postalCode: '',
    city: '',
    country: 'DE',
  },
  contactName: null,
  contactEmail: null,
  contactPhone: null,
  isActive: true,
};
import { MaintenanceFormPage } from '@/products/maintenance/features/maintenance-form-page';
import {
  useCreateMaintenanceCustomer,
  useMaintenanceCustomer,
  useUpdateMaintenanceCustomer,
} from '@/products/maintenance/features/use-maintenance';
import {
  MAINTENANCE_CUSTOMERS_PATH,
  maintenanceCustomerPath,
} from '@/products/maintenance/maintenance-paths';

function customerToFormValues(row: MaintenanceCustomer): CreateMaintenanceCustomerRequest {
  return {
    name: row.name,
    customerNumber: row.customerNumber,
    address: {
      line1: row.address.line1,
      line2: row.address.line2,
      postalCode: row.address.postalCode,
      city: row.address.city,
      country: row.address.country,
    },
    contactName: row.contactName,
    contactEmail: row.contactEmail,
    contactPhone: row.contactPhone,
    isActive: row.isActive,
  };
}

export function MaintenanceCustomerFormPage() {
  const { customerId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = location.pathname.endsWith('/bearbeiten');
  const isCreate = location.pathname.endsWith('/neu');

  usePageTitle(isEdit ? 'Kunde bearbeiten' : 'Kunde anlegen');

  const customer = useMaintenanceCustomer(isEdit ? customerId : '');
  const create = useCreateMaintenanceCustomer();
  const update = useUpdateMaintenanceCustomer(customerId);
  const pending = create.isPending || update.isPending;

  const form = useForm<CreateMaintenanceCustomerRequest>({
    resolver: zodResolver(
      createMaintenanceCustomerRequestSchema,
    ) as Resolver<CreateMaintenanceCustomerRequest>,
    defaultValues: emptyMaintenanceCustomerFormValues,
  });

  useEffect(() => {
    if (!isEdit || !customer.data) {
      return;
    }
    form.reset(customerToFormValues(customer.data));
  }, [customer.data, form, isEdit]);

  if (!isCreate && !isEdit) {
    return null;
  }

  if (isEdit && customer.isPending) {
    return <p className="text-sm text-muted-foreground">Kunde wird geladen …</p>;
  }
  if (isEdit && (customer.isError || !customer.data)) {
    return (
      <p className="text-sm text-destructive" role="alert">
        Der Kunde wurde nicht gefunden.
      </p>
    );
  }

  const cancelHref = isEdit ? maintenanceCustomerPath(customerId) : MAINTENANCE_CUSTOMERS_PATH;

  async function onValid(values: CreateMaintenanceCustomerRequest) {
    try {
      if (isEdit) {
        const saved = await update.mutateAsync(values);
        toast.success('Kunde gespeichert.');
        void navigate(maintenanceCustomerPath(saved.id));
        return;
      }
      const created = await create.mutateAsync(values);
      toast.success('Kunde angelegt.');
      void navigate(maintenanceCustomerPath(created.id));
    } catch (err) {
      const message =
        err instanceof ApiRequestError ? err.message : 'Der Kunde konnte nicht gespeichert werden.';
      toast.error(message);
    }
  }

  return (
    <FormProvider {...form}>
      <MaintenanceFormPage
        title={isEdit ? 'Kunde bearbeiten' : 'Kunde anlegen'}
        description="Stammdaten, Adresse und Ansprechpartner für Wartungsprojekte."
        cancelHref={cancelHref}
        pending={pending}
        onSubmit={() => {
          void form.handleSubmit(onValid)();
        }}
      >
        <MaintenanceCustomerStammdatenFields />
        <MaintenanceCustomerAddressFields />
        <MaintenanceCustomerContactFields />
      </MaintenanceFormPage>
    </FormProvider>
  );
}
